import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Box, CircularProgress } from "@mui/material";
import AmCharts from "../../chart/amcharts";
import { GRAPH_TYPE } from "../../../constants/trendGraph/trendGraphType";
import { DASHBOARD_SEP_TREND_TYPE } from "../constants";
import { useTranslation } from "react-i18next";
import { SEP_CHART_TYPE } from "../../../constants/trendGraph/sepLines";
import {
  craneDataProcess,
  fetchCrane,
  fetchLeg,
  fetchThruster,
  fetchTrimHeel,
  legDataProcess,
  thrusterDataProcess,
  trimHeelProcess,
} from "../../../hooks/useSepTrendData";
import { createCSV, createCsvDownload } from "../../../helpers/csvDownload/createCSV";
import { getCurrentFormattedDateTimeTextWithTimezoneForCSV } from "../../../util/timezone";
import { selectedTimezone } from "../../../model/slice/timezoneSlice";
import { calDurations } from "../../../constants/calDuration";

const onCSVButtonClickHandler = async ({ graphData, vesselName, chartName, timezone }) => {
  const blob = await createCSV(graphData, ",");
  const fileName = `${vesselName}_sep_${chartName}_${getCurrentFormattedDateTimeTextWithTimezoneForCSV(timezone)}`;
  createCsvDownload({ blob, filename: fileName });
};

export const SepTrendWidget = (props) => {
  const { vessel, selectedChart, index, startDate, endDate } = props;
  const { t } = useTranslation();
  const timezone = useSelector(selectedTimezone);
  const unmount = useRef();
  const [graphData, setGraphData] = useState([]);
  const [isDataProcessing, setIsDataProcessing] = useState(false);
  const [fetchedData, setFetchedData] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const graphName = DASHBOARD_SEP_TREND_TYPE.find((e) => e.chartType === selectedChart).name;

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const fetchData = async ({ vessel, chart, startDate, endDate, interval }) => {
      const vesselId = vessel.id;
      setIsFetching(true);
      const fetcher = async () => {
        switch (chart) {
          case SEP_CHART_TYPE.LEGS:
            return fetchLeg(vesselId, startDate, endDate, interval, vessel.NoLeg, signal);
          case SEP_CHART_TYPE.CRANE1:
            return fetchCrane(vesselId, startDate, endDate, interval, vessel.NoCrane, signal);
          case SEP_CHART_TYPE.CRANE2:
            return fetchCrane(vesselId, startDate, endDate, interval, vessel.NoCrane, signal);
          case SEP_CHART_TYPE.THRUSTER:
            return fetchThruster(vesselId, startDate, endDate, interval, vessel.NoThruster, signal);
          case SEP_CHART_TYPE.TRIM_AND_HEEL:
            return fetchTrimHeel(vesselId, startDate, endDate, interval, signal);
          default:
            return null;
        }
      };
      const data = await fetcher();
      setFetchedData({ [chart]: data });
      setIsFetching(false);
    };
    const interval = calDurations({ startDate, endDate });
    fetchData({ vessel, chart: selectedChart, startDate, endDate, interval });
  }, [startDate, endDate]);

  useEffect(() => {
    if (isFetching) return;
    if (!fetchedData) return;
    unmount.current = false;
    setIsDataProcessing(true);
    const processedData = fetchedData[selectedChart] ?? [];
    switch (selectedChart) {
      case SEP_CHART_TYPE.LEGS:
        legDataProcess(processedData, unmount, setGraphData).then(() => setIsDataProcessing(false));
        break;
      case SEP_CHART_TYPE.THRUSTER:
        thrusterDataProcess(processedData, unmount, setGraphData, vessel.NoThruster).then(() =>
          setIsDataProcessing(false)
        );
        break;
      case SEP_CHART_TYPE.CRANE1:
        craneDataProcess(processedData, setGraphData, 1).then(() => setIsDataProcessing(false));
        break;
      case SEP_CHART_TYPE.CRANE2:
        craneDataProcess(processedData, setGraphData, 2).then(() => setIsDataProcessing(false));
        break;
      case SEP_CHART_TYPE.TRIM_AND_HEEL:
        trimHeelProcess(processedData, setGraphData).then(() => setIsDataProcessing(false));
    }
    return () => {
      unmount.current = true;
    };
  }, [isFetching]);
  return (
    <Box sx={{ height: "100%", padding: "8px 16px" }}>
      <h2 style={{ color: "white" }}>{t(graphName)}</h2>
      <button
        name="csvDownloadButton"
        style={{ visibility: "hidden" }}
        onClick={() => {
          onCSVButtonClickHandler({
            graphData,
            vesselName: vessel.name,
            chartName: t(graphName),
            timezone,
          });
        }}
      />
      <Box sx={{ height: "calc(100% - 30px)", minHeight: "100px" }}>
        {isFetching || isDataProcessing ? (
          <CircularProgress />
        ) : (
          <AmCharts
            graphData={graphData}
            graphDataDigital={[]}
            vesselId={vessel.id}
            chartType={selectedChart}
            type={GRAPH_TYPE.SEP}
            isComparison={false}
            vesselData={vessel}
            isMockPreview={false}
            expand={true}
            multipleGraphIndex={index}
            isVesselOverview={false}
            isDashboard={true}
            overviewTrendIdx={0}
          />
        )}
      </Box>
    </Box>
  );
};

SepTrendWidget.propTypes = {
  vessel: PropTypes.object,
  selectedChart: PropTypes.string,
  index: PropTypes.number,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
};
